"use client"

import type React from "react"

import { useState, useEffect } from "react"
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  Video,
  X,
  Calendar,
  CheckCircle,
  ArrowLeft,
  Globe,
  ChevronDown,
  AlertCircle
} from "lucide-react"
import {
  Container,
  MainCard,
  CardContent,
  LeftPanel,
  RightPanel,
  CloseButton,
  ProfileSection,
  Avatar,
  ProfileName,
  Title,
  InfoSection,
  InfoItem,
  Description,
  SectionTitle,
  MonthNavigation,
  NavButton,
  MonthTitle,
  CalendarGrid,
  DayHeader,
  CalendarDaysGrid,
  DayCell,
  DayButton,
  CalendarAndTimeSlotsContainer,
  CalendarSection,
  TimeSlotsSection,
  TimeSlotsHeader,
  TimeSlotsTitle,
  TimeSlotsGrid,
  TimeSlotButton,
  TimeZoneSection,
  TimeZoneLabel,
  TimeZoneDropdown,
  TimeZoneButton,
  TimeZoneOptions,
  ConfirmationContainer,
  ConfirmationCard,
  ConfirmationIcon,
  ConfirmationTitle,
  ConfirmationSubtitle,
  ConfirmationDetails,
  ConfirmationDetailsTitle,
  ConfirmationDetailsList,
  ConfirmationDetailItem,
  CloseConfirmation,
  LoadingSpinner,
  EmptyState,
  SelectedTimezoneDisplay,
  TimeZoneSearchContainer,
  TimeZoneSearchInput,
  TimeZoneScrollableContent,
  TimeZoneGroupHeader,
  TimeZoneOptionWithTime,
  TimeZoneOptionLabel,
  TimeZoneOptionTime,
  BookingDetailsContainer,
  BookingDetailsCard,
  BookingDetailsContent,
  BookingDetailsLeft,
  BookingDetailsRight,
  BackButton,
  BookingDetailsHeader,
  FormSection,
  FormLabel,
  FormInput,
  FormTextarea,
  AddGuestsButton,
  ScheduleButton,
  MeetingDetailsList,
  MeetingDetailItem,
  GuestEmailContainer,
  GuestEmailTags,
  GuestEmailTagText,
  RemoveGuestButton,
  GuestEmailTag, 
  MobileContainer,
  MobileHeader,
  MobileBackButton,
  MobileTitle,
  MobileContent,
  MobileFooter,
  TimeSlotRow,
  NextButton,
  InlineErrorIcon,
  ErrorTooltip,
  FormInputContainer,
} from "./styles"
import { timezones } from "./timezones"
import useCustomCalendarListener from "../../../../hooks/use-custom-calendar-listener"
import type { ScheduleMeetingData } from "../../../../../common/store/theme/types/schedule-meeting"
import { scheduleMeeting } from "../../../../utils/schedule-meeting"
import { validateEmail } from "../../../../utils/validators"
import { convertAvailableSlotsToTimezone } from "./convert-slots-to-timezone"
import { useAppSelector } from "../../../../../common/store"
import { AvailableSlots } from "../../../../../common/store/theme/types/available-slots"
import { AgentDetails } from "../../../../../common/store/theme/types/agent-details"
import { MeetingDetails } from "../../../../../common/store/theme/types/meeting-details"

interface CustomCalendarSchedulerProps {
  agentDetails: AgentDetails | undefined
  availableSlots: AvailableSlots
  meetingDetails: MeetingDetails | undefined
  onClose: () => void
}

export default function CustomCalendarScheduler({
  agentDetails,
  availableSlots,
  meetingDetails,
  onClose,
}: CustomCalendarSchedulerProps) {
  if (agentDetails === undefined || meetingDetails === undefined || Object.keys(availableSlots).length === 0) {
    return (
      <div style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: "100vh",
      width: "100vw",
      }}>
      <div style={{ transform: "scale(2)" }}>
        <LoadingSpinner />
      </div>
      </div>
    )
  }
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string | null>(null)
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date()
    return new Date(now.getFullYear(), now.getMonth())
  })
  const [showTimeZoneOptions, setShowTimeZoneOptions] = useState(false)
  const getSystemTimeZone = () => {
    if (typeof Intl !== "undefined" && Intl.DateTimeFormat) {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Los_Angeles"
    }
    return "America/Los_Angeles"
  }
  const [selectedTimeZone, setSelectedTimeZone] = useState(getSystemTimeZone())
  const [showBookingDetails, setShowBookingDetails] = useState(false)
  const [bookingConfirmed, setBookingConfirmed] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const { onCloseClick, onEventTypeViewed, onDateAndTimeSelected, onEventScheduled } = useCustomCalendarListener(onClose)
  const [searchQuery, setSearchQuery] = useState("")
  const [nameError, setNameError] = useState("")
  const [mainEmailError, setMainEmailError] = useState("")
  const [guestEmailError, setGuestEmailError] = useState("")
  const {agentEmail } = useAppSelector((state) => state.theme.themeConfig);

  // Mobile step state
  const [isMobile, setIsMobile] = useState(false)
  const [mobileStep, setMobileStep] = useState(1) // 1: Calendar, 2: Time slots, 3: Form
  
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
    addGuests: false,
    guests: [] as string[], // Changed to array
  })
  
  let convertedAvailableSlots = convertAvailableSlotsToTimezone(availableSlots, selectedTimeZone)
  useEffect(() => {
    convertedAvailableSlots = convertAvailableSlotsToTimezone(availableSlots, selectedTimeZone)
  },[selectedTimeZone, availableSlots])
  // Guest email input state
  const [guestEmailInput, setGuestEmailInput] = useState("")

  // Check if mobile on mount and resize
  useEffect(() => {
    onEventTypeViewed()
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768)
    }

    checkMobile()
    window.addEventListener("resize", checkMobile)

    return () => window.removeEventListener("resize", checkMobile)
  }, [])


  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ]

  const daysOfWeek = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"]

  // Add guest email
  const addGuestEmail = () => {
    const email = guestEmailInput.trim()
    setGuestEmailError("")

    if (!email) return

    if (!validateEmail(email)) {
      setGuestEmailError("Please enter a valid guest email address")
      return
    }

    if (formData.guests.includes(email)) {
      setGuestEmailError("This guest email is already added")
      return
    }

    setFormData({
      ...formData,
      guests: [...formData.guests, email],
    })
    setGuestEmailInput("")
  }

  // Remove guest email
  const removeGuestEmail = (emailToRemove: string) => {
    setFormData({
      ...formData,
      guests: formData.guests.filter((email) => email !== emailToRemove),
    })
  }

  // Handle guest email input key press
  const handleGuestEmailKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault()
      addGuestEmail()
    }
  }

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()
    const startingDayOfWeek = (firstDay.getDay() + 6) % 7

    const days = []
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null)
    }
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day)
    }
    return days
  }

  const isDateAvailable = (day: number) => {
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`
    return convertedAvailableSlots[dateStr] && convertedAvailableSlots[dateStr].length > 0
  }

  const navigateMonth = (direction: string) => {
    setCurrentMonth((prev) => {
      const newDate = new Date(prev)
      if (direction === "prev") {
        newDate.setMonth(prev.getMonth() - 1)
      } else {
        newDate.setMonth(prev.getMonth() + 1)
      }
      return newDate
    })
    setSelectedDate(null)
    setSelectedTimeSlot(null)
  }

  const handleDateSelect = (day: number) => {
    if (isDateAvailable(day)) {
      const newDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day)
      setSelectedDate(newDate)
      setSelectedTimeSlot(null)
      // On mobile, go to next step
      if (isMobile) {
        setMobileStep(2)
      }
    }
  }

  const handleTimeSlotSelect = (slot: string) => {
    setSelectedTimeSlot(slot)
    onDateAndTimeSelected()
  }

  const getSelectedDateSlots = () => {
    if (!selectedDate) return []
    const dateStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(2, "0")}`
    const slots = convertedAvailableSlots[dateStr] || []
    const formatter = new Intl.DateTimeFormat("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
    const localSlots = slots.map((time) => {
      const [hours, minutes] = time.split(":")
      const date = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), Number(hours), Number(minutes))
      return formatter.format(date)
    })
    return localSlots
  }

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    })
  }

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(":")
    const hour = Number.parseInt(hours)
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour
    return `${displayHour}:${minutes}`
  }

  const formatTimeRange = (time: string) => {
    const startTime = time;
    const duration = meetingDetails?.duration ? parseInt(meetingDetails.duration, 10) : 0;
    
    // Parse the start time
    const [timePart, period] = startTime.split(' ');
    const [hours, minutes] = timePart.split(':').map(Number);
        let startHour24 = hours;
    if (period === 'PM' && hours !== 12) {
      startHour24 += 12;
    } else if (period === 'AM' && hours === 12) {
      startHour24 = 0;
    }
    const startMinutes = startHour24 * 60 + minutes;
    const endMinutes = startMinutes + duration;
    const endHour24 = Math.floor(endMinutes / 60) % 24;
    const endMin = endMinutes % 60;
    let endHour12 = endHour24;
    let endPeriod = 'AM';
    if (endHour24 === 0) {
      endHour12 = 12;
    } else if (endHour24 === 12) {
      endPeriod = 'PM';
    } else if (endHour24 > 12) {
      endHour12 = endHour24 - 12;
      endPeriod = 'PM';
    }
    const endTime = `${endHour12}:${endMin.toString().padStart(2, '0')} ${endPeriod}`;
    return `${startTime} - ${endTime}`;
  };

  const getCurrentTimeInTimezone = (timezone: string) => {
    const now = new Date()
    const timeInZone = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).format(now)
    return timeInZone
  }

  const filteredTimezones = timezones.filter(
    (tz) =>
      tz.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tz.value.toLowerCase().includes(searchQuery.toLowerCase()),
  )

  const groupedTimezones = filteredTimezones.reduce(
    (groups, timezone) => {
      const region = timezone.value.split("/")[0]
      if (!groups[region]) {
        groups[region] = []
      }
      groups[region].push(timezone)
      return groups
    },
    {} as Record<string, typeof timezones>,
  )

  const handleBooking = () => {
    setShowBookingDetails(true)
  }
  const handleScheduleEvent = async () => {
    // Reset all error states
    setNameError("")
    setMainEmailError("")

    let hasErrors = false

    if (!formData.name) {
      setNameError("Name is required")
      hasErrors = true
    }

    if (!formData.email) {
      hasErrors = true
    } else if (!validateEmail(formData.email)) {
      setMainEmailError("Please enter a valid email address")
      hasErrors = true
    }

    const invalidGuestEmails = formData.guests.filter((email) => !validateEmail(email))
    if (invalidGuestEmails.length > 0) {
      hasErrors = true
    }

    if (hasErrors) return

    const payload: ScheduleMeetingData = {
      ...formData,
      selectedDate: selectedDate?.toDateString() ?? "",
      selectedTimeSlot: selectedTimeSlot ?? "",
      selectedTimeZone,
      agent_email: agentEmail || "", 
    }

    try {
      setIsLoading(true)
      const response = await scheduleMeeting(payload)
      onEventScheduled(payload)
      setShowBookingDetails(false)
      setBookingConfirmed(true)
      setMobileStep(1)
    } catch (error) {
      console.error("Error scheduling meeting:", error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    onCloseClick()
    setSelectedDate(null)
    setSelectedTimeSlot(null)
    setCurrentMonth(new Date())
    setShowTimeZoneOptions(false)
    setSelectedTimeZone("America/Los_Angeles")
    setShowBookingDetails(false)
    setBookingConfirmed(false)
    setIsLoading(false)
    setFormData({ name: "", email: "", message: "", addGuests: false, guests: [] })
    setGuestEmailInput("")
    setMobileStep(1)
  }

  const handleBackToCalendar = () => {
    setShowBookingDetails(false)
  }

  const handleMobileBack = () => {
    if (mobileStep > 1) {
      setMobileStep(mobileStep - 1)
    }
  }

  // Mobile Step 1: Calendar View
  const renderMobileCalendarStep = () => (
    <MobileContainer>
      <MobileHeader>
        <CloseButton onClick={handleClose}>
          <X size={20} />
        </CloseButton>
        <MobileTitle>Select a Day</MobileTitle>
      </MobileHeader>

      <MobileContent>
        <ProfileSection>
          <Avatar>
            {agentDetails?.agentPhoto ? (
              <img
                src={agentDetails?.agentPhoto || "/placeholder.svg"}
                alt={agentDetails?.agentName}
                style={{ width: "100%", height: "100%", borderRadius: "50%" }}
              />
            ) : (
              <span>
                {agentDetails?.agentName
                  .split(" ")
                  .map((n) => n[0])
                  .join("")
                  .toUpperCase()}
              </span>
            )}
          </Avatar>
          <div>
            <ProfileName>{agentDetails?.agentName}</ProfileName>
            <Title>{meetingDetails?.title}</Title>
          </div>
        </ProfileSection>

        <InfoSection>
          <InfoItem>
            <Clock size={16} />
            <span>{meetingDetails?.duration + " min"}</span>
          </InfoItem>
          <InfoItem alignStart>
            <Video size={16} />
            <span>Meeting details provided upon confirmation.</span>
          </InfoItem>
        </InfoSection>

        <MonthNavigation>
          <NavButton onClick={() => navigateMonth("prev")}>
            <ChevronLeft size={16} />
          </NavButton>
          <MonthTitle>
            {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </MonthTitle>
          <NavButton onClick={() => navigateMonth("next")}>
            <ChevronRight size={16} />
          </NavButton>
        </MonthNavigation>

        <CalendarGrid>
          {daysOfWeek.map((day) => (
            <DayHeader key={day}>{day}</DayHeader>
          ))}
        </CalendarGrid>

        <CalendarDaysGrid>
          {getDaysInMonth(currentMonth).map((day, index) => (
            <DayCell key={index}>
              {day && (
                <DayButton
                  isSelected={!!selectedDate && selectedDate.getDate() === day}
                  isAvailable={isDateAvailable(day)}
                  disabled={!isDateAvailable(day)}
                  onClick={() => handleDateSelect(day)}
                >
                  {day}
                </DayButton>
              )}
            </DayCell>
          ))}
        </CalendarDaysGrid>

        <TimeZoneSection>
          <TimeZoneLabel>Time zone</TimeZoneLabel>
          <TimeZoneDropdown>
            <TimeZoneButton onClick={() => setShowTimeZoneOptions(!showTimeZoneOptions)}>
              <SelectedTimezoneDisplay>
                <Globe size={16} />
                <span>{timezones.find((tz) => tz.value === selectedTimeZone)?.label || "Select Timezone"}</span>
              </SelectedTimezoneDisplay>
              <ChevronDown
                size={16}
                style={{
                  transform: showTimeZoneOptions ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s ease",
                }}
              />
            </TimeZoneButton>
            {showTimeZoneOptions && (
              <TimeZoneOptions>
                <TimeZoneSearchContainer>
                  <TimeZoneSearchInput
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </TimeZoneSearchContainer>
                <TimeZoneScrollableContent>
                  {Object.entries(groupedTimezones).map(([region, timezones]) => (
                    <div key={region}>
                      <TimeZoneGroupHeader>{region.replace("_", " ")}</TimeZoneGroupHeader>
                      {timezones.map((option) => (
                        <TimeZoneOptionWithTime
                          key={option.value}
                          selected={selectedTimeZone === option.value}
                          onClick={() => {
                            setSelectedTimeZone(option.value)
                            setShowTimeZoneOptions(false)
                            setSearchQuery("")
                          }}
                        >
                          <TimeZoneOptionLabel>{option.label.replace(/\s*$$[^)]*$$/, "")}</TimeZoneOptionLabel>
                          <TimeZoneOptionTime>{getCurrentTimeInTimezone(option.value)}</TimeZoneOptionTime>
                        </TimeZoneOptionWithTime>
                      ))}
                    </div>
                  ))}
                </TimeZoneScrollableContent>
              </TimeZoneOptions>
            )}
          </TimeZoneDropdown>
        </TimeZoneSection>
      </MobileContent>
    </MobileContainer>
  )

  // Mobile Step 2: Time Slot Selection
  const renderMobileTimeSlotStep = () => (
    <MobileContainer>
      <MobileHeader>
        <MobileBackButton onClick={handleMobileBack}>
          <ArrowLeft size={20} />
        </MobileBackButton>
        <MobileTitle>Select a Time</MobileTitle>
      </MobileHeader>

      <MobileContent>
        <div style={{ textAlign: "center", marginBottom: "2rem" }}>
          <h3 style={{ margin: "0 0 0.5rem 0", fontSize: "1.25rem", fontWeight: "600" }}>
            {selectedDate && formatDate(selectedDate)}
          </h3>
          <p style={{ margin: 0, color: "#6b7280", fontSize: "0.875rem" }}>
            Duration: {meetingDetails?.duration + " min"}
          </p>
        </div>

        <TimeSlotsGrid>
          {getSelectedDateSlots().length > 0 ? (
            getSelectedDateSlots().map((slot) => (
              <TimeSlotRow key={slot}>
                <TimeSlotButton
                  onClick={() => handleTimeSlotSelect(slot)}
                  isSelected={selectedTimeSlot === slot}
                  style={{ flex: 1 }}
                >
                  {formatTime(slot)}
                </TimeSlotButton>
                {selectedTimeSlot === slot && <NextButton onClick={() => setMobileStep(3)}>Next</NextButton>}
              </TimeSlotRow>
            ))
          ) : (
            <EmptyState>
              <Clock size={24} />
              <span>No available times for this date</span>
            </EmptyState>
          )}
        </TimeSlotsGrid>
      </MobileContent>
    </MobileContainer>
  )

  // Mobile Step 3: Form
  const renderMobileFormStep = () => (
    <MobileContainer>
      <MobileHeader>
        <MobileBackButton onClick={handleMobileBack}>
          <ArrowLeft size={20} />
        </MobileBackButton>
        <MobileTitle>Enter Details</MobileTitle>
      </MobileHeader>

      <MobileContent>
        <div style={{ marginBottom: "2rem" }}>
          <ProfileName>{agentDetails?.agentName}</ProfileName>
          <Title style={{ marginBottom: "1rem" }}>{meetingDetails?.title}</Title>

          <MeetingDetailsList>
            <MeetingDetailItem>
              <Clock size={16} />
              <span>{meetingDetails?.duration + " min"}</span>
            </MeetingDetailItem>
            <MeetingDetailItem>
              <Calendar size={16} />
              <span>
                {selectedTimeSlot && formatTimeRange(selectedTimeSlot)}, {selectedDate && formatDate(selectedDate)}
              </span>
            </MeetingDetailItem>
            <MeetingDetailItem>
              <Globe size={16} />
              <span>{timezones.find((tz) => tz.value === selectedTimeZone)?.label}</span>
            </MeetingDetailItem>
          </MeetingDetailsList>
        </div>

        <FormSection>
          <FormLabel>
            Name <span style={{ color: "#ef4444" }}>*</span>
          </FormLabel>
          <FormInputContainer>
            <FormInput
              type="text"
              value={formData.name}
              onChange={(e) => {
                setFormData({ ...formData, name: e.target.value })
                if (e.target.value) setNameError("")
              }}
              required
              hasError={!!nameError}
            />
            {nameError && (
              <>
                <InlineErrorIcon>
                  <AlertCircle size={16} />
                </InlineErrorIcon>
                <ErrorTooltip>{nameError}</ErrorTooltip>
              </>
            )}
          </FormInputContainer>
        </FormSection>

        <FormSection>
          <FormLabel>
            Email <span style={{ color: "#ef4444" }}>*</span>
          </FormLabel>
          <FormInputContainer>
            <FormInput
              type="email"
              value={formData.email}
              onChange={(e) => {
                setFormData({ ...formData, email: e.target.value })
                if (e.target.value) setMainEmailError("")
              }}
              required
              hasError={!!mainEmailError}
            />
            {mainEmailError && (
              <>
                <InlineErrorIcon>
                  <AlertCircle size={16} />
                </InlineErrorIcon>
                <ErrorTooltip>{mainEmailError}</ErrorTooltip>
              </>
            )}
          </FormInputContainer>
        </FormSection>

        {formData.addGuests ? (
          <FormSection>
            <FormLabel>Guest Email(s)</FormLabel>
            <GuestEmailContainer>
              <GuestEmailTags>
                {formData.guests.map((email, index) => (
                  <GuestEmailTag key={index}>
                    <GuestEmailTagText>{email}</GuestEmailTagText>
                    <RemoveGuestButton onClick={() => removeGuestEmail(email)}>
                      <X size={14} />
                    </RemoveGuestButton>
                  </GuestEmailTag>
                ))}
              </GuestEmailTags>
              <FormInputContainer>
                <FormInput
                  type="email"
                  value={guestEmailInput}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setGuestEmailInput(e.target.value)
                    if (guestEmailError) setGuestEmailError("")
                  }}
                  onKeyDown={handleGuestEmailKeyPress}
                  onBlur={addGuestEmail}
                  placeholder="Enter email and press Enter"
                  hasError={!!guestEmailError}
                />
                {guestEmailError && (
                  <>
                    <InlineErrorIcon>
                      <AlertCircle size={16} />
                    </InlineErrorIcon>
                    <ErrorTooltip>{guestEmailError}</ErrorTooltip>
                  </>
                )}
              </FormInputContainer>
            </GuestEmailContainer>
          </FormSection>
        ) : (
          <AddGuestsButton onClick={() => setFormData({ ...formData, addGuests: true })}>Add Guests</AddGuestsButton>
        )}

        <FormSection>
          <FormLabel>Please share anything that will help prepare for our meeting.</FormLabel>
          <FormTextarea
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            rows={4}
          />
        </FormSection>
      </MobileContent>

      <MobileFooter>
        <ScheduleButton onClick={handleScheduleEvent} disabled={isLoading || !formData.name || !formData.email}>
          {isLoading ? (
            <>
              <LoadingSpinner />
              Scheduling...
            </>
          ) : (
            "Schedule Meeting"
          )}
        </ScheduleButton>
      </MobileFooter>
    </MobileContainer>
  )

  // Booking Details Form (Desktop)
  if (showBookingDetails && selectedDate && selectedTimeSlot && !isMobile) {
    return (
      <BookingDetailsContainer>
        <BookingDetailsCard>
          <BookingDetailsContent>
            <BookingDetailsLeft>
              <div style={{ marginTop: "2rem" }}>
                <ProfileName>{agentDetails?.agentName}</ProfileName>
                <Title style={{ marginBottom: "1.5rem" }}>{meetingDetails?.title}</Title>

                <MeetingDetailsList>
                  <MeetingDetailItem>
                    <Clock size={16} />
                    <span>{meetingDetails?.duration + " min"}</span>
                  </MeetingDetailItem>

                  <MeetingDetailItem>
                    <Video size={16} />
                    <span>Meeting details provided upon confirmation.</span>
                  </MeetingDetailItem>

                  <MeetingDetailItem>
                    <Calendar size={16} />
                    <span>
                      {formatTimeRange(selectedTimeSlot)}, {formatDate(selectedDate)}
                    </span>
                  </MeetingDetailItem>

                  <MeetingDetailItem>
                    <Globe size={16} />
                    <span>{timezones.find((tz) => tz.value === selectedTimeZone)?.label}</span>
                  </MeetingDetailItem>
                  <MeetingDetailItem style={{ marginTop: "1rem" }}>
                    <span>{meetingDetails?.description}</span>
                  </MeetingDetailItem>
                </MeetingDetailsList>
              </div>

              <BackButton onClick={handleBackToCalendar}>
                <ArrowLeft size={20} />
                <span style={{ marginLeft: "0.5rem" }}>Back</span>
              </BackButton>
            </BookingDetailsLeft>

            <BookingDetailsRight>
              <CloseButton onClick={handleClose}>
                <X size={20} />
              </CloseButton>

              <BookingDetailsHeader>Enter Details</BookingDetailsHeader>

              <FormSection>
                <FormLabel>
                  Name <span style={{ color: "#ef4444" }}>*</span>
                </FormLabel>
                <FormInputContainer>
                  <FormInput
                    type="text"
                    value={formData.name}
                    onChange={(e) => {
                      setFormData({ ...formData, name: e.target.value })
                      if (e.target.value) setNameError("")
                    }}
                    required
                    hasError={!!nameError}
                  />
                  {nameError && (
                    <>
                      <InlineErrorIcon>
                        <AlertCircle size={16} />
                      </InlineErrorIcon>
                      <ErrorTooltip>{nameError}</ErrorTooltip>
                    </>
                  )}
                </FormInputContainer>
              </FormSection>

              <FormSection>
                <FormLabel>
                  Email <span style={{ color: "#ef4444" }}>*</span>
                </FormLabel>
                <FormInputContainer>
                  <FormInput
                    type="email"
                    value={formData.email}
                    onChange={(e) => {
                      setFormData({ ...formData, email: e.target.value })
                      if (e.target.value) setMainEmailError("")
                    }}
                    required
                    placeholder="Enter your work email"
                    hasError={!!mainEmailError}
                  />
                  {mainEmailError && (
                    <>
                      <InlineErrorIcon>
                        <AlertCircle size={16} />
                      </InlineErrorIcon>
                      <ErrorTooltip>{mainEmailError}</ErrorTooltip>
                    </>
                  )}
                </FormInputContainer>
              </FormSection>

              {formData.addGuests ? (
                <FormSection>
                  <FormLabel>Guest Email(s)</FormLabel>
                  <GuestEmailContainer>
                    <GuestEmailTags>
                      {formData.guests.map((email, index) => (
                        <GuestEmailTag key={index}>
                          <GuestEmailTagText>{email}</GuestEmailTagText>
                          <RemoveGuestButton onClick={() => removeGuestEmail(email)}>
                            <X size={14} />
                          </RemoveGuestButton>
                        </GuestEmailTag>
                      ))}
                    </GuestEmailTags>
                    <FormInputContainer>
                      <FormInput
                        type="email"
                        value={guestEmailInput}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setGuestEmailInput(e.target.value)
                          if (guestEmailError) setGuestEmailError("")
                        }}
                        onKeyDown={handleGuestEmailKeyPress}
                        onBlur={addGuestEmail}
                        placeholder="Enter email and press Enter"
                        hasError={!!guestEmailError}
                      />
                      {guestEmailError && (
                        <>
                          <InlineErrorIcon>
                            <AlertCircle size={16} />
                          </InlineErrorIcon>
                          <ErrorTooltip>{guestEmailError}</ErrorTooltip>
                        </>
                      )}
                    </FormInputContainer>
                  </GuestEmailContainer>
                </FormSection>
              ) : (
                <AddGuestsButton onClick={() => setFormData({ ...formData, addGuests: true })}>
                  Add Guests
                </AddGuestsButton>
              )}

              <FormSection>
                <FormLabel>Please share anything that will help prepare for our meeting.</FormLabel>
                <FormTextarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows={4}
                />
              </FormSection>

              <ScheduleButton onClick={handleScheduleEvent} disabled={isLoading || !formData.name || !formData.email}>
                {isLoading ? (
                  <>
                    <LoadingSpinner />
                    Scheduling...
                  </>
                ) : (
                  "Schedule Meeting"
                )}
              </ScheduleButton>
            </BookingDetailsRight>
          </BookingDetailsContent>
        </BookingDetailsCard>
      </BookingDetailsContainer>
    )
  }

  // Final Confirmation
  if (bookingConfirmed && selectedDate && selectedTimeSlot) {
    return (
      <ConfirmationContainer>
        <ConfirmationCard>
          <CloseConfirmation onClick={() => handleClose()}>
            <X size={20} />
          </CloseConfirmation>
          <ConfirmationIcon>
            <CheckCircle />
            <ConfirmationTitle>Booking Confirmed!</ConfirmationTitle>
            <ConfirmationSubtitle>Your meeting has been successfully scheduled.</ConfirmationSubtitle>
          </ConfirmationIcon>

          <ConfirmationDetails>
            <ConfirmationDetailsTitle>Meeting Details</ConfirmationDetailsTitle>
            <ConfirmationDetailsList>
              <ConfirmationDetailItem>
                <Calendar size={16} />
                <span>{selectedDate && formatDate(selectedDate)}</span>
              </ConfirmationDetailItem>
              <ConfirmationDetailItem>
                <Clock size={16} />
                <span>
                  {selectedTimeSlot && formatTime(selectedTimeSlot)} ({meetingDetails?.duration + " min"})
                </span>
              </ConfirmationDetailItem>
              <ConfirmationDetailItem>
                <Video size={16} />
                <span>Meeting Link will be sent via email</span>
              </ConfirmationDetailItem>
            </ConfirmationDetailsList>
          </ConfirmationDetails>
        </ConfirmationCard>
      </ConfirmationContainer>
    )
  }

  // Mobile Flow
  if (isMobile) {
    if (mobileStep === 1) return renderMobileCalendarStep()
    if (mobileStep === 2) return renderMobileTimeSlotStep()
    if (mobileStep === 3) return renderMobileFormStep()
  }

  // Desktop Main Calendar View
  return (
    <Container>
      <MainCard hasSelectedDate={!!selectedDate}>
        <CardContent>
          <LeftPanel>
            <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
              <ProfileSection>
                <Avatar>
                  {agentDetails?.agentPhoto ? (
                    <img
                      src={agentDetails?.agentPhoto || "/placeholder.svg"}
                      alt={agentDetails?.agentName}
                      style={{ width: "100%", height: "100%", borderRadius: "50%" }}
                    />
                  ) : (
                    <span>
                      {agentDetails?.agentName
                        .split(" ")
                        .map((n) => n[0])
                        .join("")
                        .toUpperCase()}
                    </span>
                  )}
                </Avatar>
                <div>
                  <ProfileName>{agentDetails?.agentName}</ProfileName>
                </div>
              </ProfileSection>

              <div>
                <Title>{meetingDetails?.title}</Title>
              </div>

              <InfoSection>
                <InfoItem>
                  <Clock size={16} />
                  <span>{meetingDetails?.duration + " min"}</span>
                </InfoItem>

                <InfoItem alignStart>
                  <Video size={16} />
                  <span>Meeting details provided upon confirmation.</span>
                </InfoItem>
              </InfoSection>

              <Description>{meetingDetails?.description}</Description>

              <TimeZoneSection>
                <TimeZoneLabel>Time zone</TimeZoneLabel>
                <TimeZoneDropdown>
                  <TimeZoneButton onClick={() => setShowTimeZoneOptions(!showTimeZoneOptions)}>
                    <SelectedTimezoneDisplay>
                      <Globe size={16} />
                      <span>{timezones.find((tz) => tz.value === selectedTimeZone)?.label || "Select Timezone"}</span>
                    </SelectedTimezoneDisplay>
                    <ChevronDown
                      size={16}
                      style={{
                        transform: showTimeZoneOptions ? "rotate(180deg)" : "rotate(0deg)",
                        transition: "transform 0.2s ease",
                      }}
                    />
                  </TimeZoneButton>
                  {showTimeZoneOptions && (
                    <TimeZoneOptions>
                      <TimeZoneSearchContainer>
                        <TimeZoneSearchInput
                          type="text"
                          placeholder="Search..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                        />
                      </TimeZoneSearchContainer>
                      <TimeZoneScrollableContent>
                        {Object.entries(groupedTimezones).map(([region, timezones]) => (
                          <div key={region}>
                            <TimeZoneGroupHeader>{region.replace("_", " ")}</TimeZoneGroupHeader>
                            {timezones.map((option) => (
                              <TimeZoneOptionWithTime
                                key={option.value}
                                selected={selectedTimeZone === option.value}
                                onClick={() => {
                                  setSelectedTimeZone(option.value)
                                  setShowTimeZoneOptions(false)
                                  setSearchQuery("")
                                }}
                              >
                                <TimeZoneOptionLabel>{option.label.replace(/\s*$$[^)]*$$/, "")}</TimeZoneOptionLabel>
                                <TimeZoneOptionTime>{getCurrentTimeInTimezone(option.value)}</TimeZoneOptionTime>
                              </TimeZoneOptionWithTime>
                            ))}
                          </div>
                        ))}
                      </TimeZoneScrollableContent>
                    </TimeZoneOptions>
                  )}
                </TimeZoneDropdown>
              </TimeZoneSection>
            </div>
          </LeftPanel>

          <RightPanel>
            <CloseButton onClick={handleClose}>
              <X size={20} />
            </CloseButton>

            <SectionTitle>Select a Date & Time</SectionTitle>

            <CalendarAndTimeSlotsContainer hasTimeSlots={!!selectedDate}>
              <CalendarSection hasTimeSlots={!!selectedDate}>
                <MonthNavigation>
                  <NavButton onClick={() => navigateMonth("prev")}>
                    <ChevronLeft size={16} />
                  </NavButton>
                  <MonthTitle>
                    {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                  </MonthTitle>
                  <NavButton onClick={() => navigateMonth("next")}>
                    <ChevronRight size={16} />
                  </NavButton>
                </MonthNavigation>

                <CalendarGrid>
                  {daysOfWeek.map((day) => (
                    <DayHeader key={day}>{day}</DayHeader>
                  ))}
                </CalendarGrid>

                <CalendarDaysGrid>
                  {getDaysInMonth(currentMonth).map((day, index) => (
                    <DayCell key={index}>
                      {day && (
                        <DayButton
                          isSelected={!!selectedDate && selectedDate.getDate() === day}
                          isAvailable={isDateAvailable(day)}
                          disabled={!isDateAvailable(day)}
                          onClick={() => handleDateSelect(day)}
                        >
                          {day}
                        </DayButton>
                      )}
                    </DayCell>
                  ))}
                </CalendarDaysGrid>
              </CalendarSection>

              {selectedDate && (
                <TimeSlotsSection isVisible={true}>
                  <TimeSlotsHeader>
                    <TimeSlotsTitle>{formatDate(selectedDate)}</TimeSlotsTitle>
                  </TimeSlotsHeader>

                  <TimeSlotsGrid>
                    {getSelectedDateSlots().length > 0 ? (
                      getSelectedDateSlots().map((slot) => (
                        <TimeSlotRow key={slot}>
                          <TimeSlotButton
                            onClick={() => setSelectedTimeSlot(slot)}
                            isSelected={selectedTimeSlot === slot}
                            style={{ flex: 1 }}
                          >
                            {formatTime(slot)}
                          </TimeSlotButton>
                          {selectedTimeSlot === slot && <NextButton onClick={handleBooking}>Next</NextButton>}
                        </TimeSlotRow>
                      ))
                    ) : (
                      <EmptyState>
                        <Clock size={24} />
                        <span>No available times for this date</span>
                      </EmptyState>
                    )}
                  </TimeSlotsGrid>
                </TimeSlotsSection>
              )}
            </CalendarAndTimeSlotsContainer>
          </RightPanel>
        </CardContent>
      </MainCard>
    </Container>
  )
}